import { captureException } from '@sentry/browser';
import { get } from 'lodash';
import { getInsuranceCards } from 'src/Booking/actions/InsuranceCardFetch';
import { shouldShowReviewAndBookScanner } from 'src/config/ab/selector';
import logToDataDog from 'src/config/logToDataDog';
import { AnalyticsTypes } from '../../middleware/google-analytics';
import { INSURANCE_NOT_TAKEN_WARNING } from '../components/AppointmentRequirements/constants';
import { BookingStateProperties } from '../constants';
import { APPOINTMENT_INFO_GA_CATEGORY } from '../pages/AppointmentInfo/constants';
import bookingStateSelector from '../selectors/BookingStateSelector';
import patientSelector from '../selectors/PatientSelector';
import reviewAndBookScanCardStateSelector from '../selectors/ReviewAndBookScanInsuranceCardSelector';
import { getCurrentInsuranceCard } from '../utils/getCurrentInsuranceCard';
import {
    GA_ACTIONS,
    INSURANCE_ID_TYPE,
    SCANNED_CARD_FETCH_MAX_RETRIES,
    SCANNED_CARD_FETCH_RETRY_TIMING,
} from './constants';
import { updateBookingState } from './SchedulingActions';

export const InsuranceCardActions = {
    SELECTED: '@@ica/CARD_SELECTED',
    SHARE_CLICKED: '@@ica/SHARE_CLICKED',
    TRACK_CLICK_CARRIER_DROPDOWN: '@@ica/TRACK_CLICK_CARRIER_DROPDOWN',
    TRACK_CLICK_PLAN_DROPDOWN: '@@ica/TRACK_CLICK_PLAN_DROPDOWN',
    TRACK_PRELOAD_CARRIER: '@@ica/TRACK_PRELOAD_CARRIER',
    TRACK_PRELOAD_PLAN: '@@ica/TRACK_PRELOAD_PLAN',
    SCANNED_CARD_SUCCESS: '@@ica/SCANNED_CARD_SUCCESS',
    SET_IS_FETCHING_CARDS: '@@ica/SET_IS_FETCHING_CARDS',
    SET_SCANNED_CARD_SCANNER_OPEN_STATE:
        '@@ica/SET_SCANNED_CARD_SCANNER_OPEN_STATE',
    SET_SCANNED_CARD_LOADING_STATE: '@@ica/SET_SCANNED_CARD_LOADING_STATE',
    SET_SCANNED_CARD_FETCH_RETRY_COUNT:
        '@@ica/SET_SCANNED_CARD_FETCH_RETRY_COUNT',
    TRACK_SCANNED_CARD_RETRY_METRICS: '@@ica/TRACK_SCANNED_CARD_RETRY_METRICS',
    SET_HAS_FETCH_SCANNED_CARD_ERROR: '@@ica/SET_HAS_FETCH_CARD_ERROR',
};

const getSelectCardGaAction = (cards, card) => {
    if (card.isDefault) {
        return cards && cards.length > 0
            ? 'Insurance Card - No Images For Plan'
            : 'Insurance Card - No Images At All';
    } else {
        return card.cardHasPlan
            ? 'Insurance Card - Displayed For Plan'
            : 'Insurance Card - Displayed With No Plan';
    }
};

const setCard = ({
    cards,
    card,
    analytics = [],
    isCarrierChange,
    isPlanChange,
}) => {
    return {
        type: InsuranceCardActions.SELECTED,
        payload: card,
        isCarrierChange,
        isPlanChange,
        meta: {
            analytics: [
                ...analytics,
                {
                    type: AnalyticsTypes.TrackEvent,
                    category: APPOINTMENT_INFO_GA_CATEGORY,
                    action: getSelectCardGaAction(cards, card),
                },
            ],
        },
    };
};

export function setPatientAndInsuranceCard(currentPatientId) {
    return (dispatch, getState) => {
        const state = getState();
        const bookingState = bookingStateSelector(state);
        const { insuranceCards } = patientSelector(state);

        const currentCard = getCurrentInsuranceCard({
            state,
            insuranceCards,
            currentPatientId,
        });

        dispatch(
            updateBookingState({
                ...bookingState,
                [BookingStateProperties.patientId]: currentPatientId,
                [BookingStateProperties.insuranceCardDataId]:
                    currentCard.availableInsuranceCardDataId,
                [BookingStateProperties.isInsuranceCardShared]: true,
            })
        );

        dispatch(
            setCard({
                cards: insuranceCards,
                card: currentCard,
            })
        );
    };
}

export function setCarrierState(carrierId) {
    return (dispatch, getState) => {
        const state = getState();
        const reviewAndBookInsuranceScannerShown =
            shouldShowReviewAndBookScanner(state);
        const bookingState = bookingStateSelector(state);
        const { insuranceCards } = patientSelector(state);

        const currentCard = getCurrentInsuranceCard({
            state,
            insuranceCards,
        });

        const requirements = get(bookingState, 'requirements', []);
        const newRequirements = requirements.filter(
            (req) => req.key !== INSURANCE_NOT_TAKEN_WARNING
        );

        const useExistingInsuranceCardDataId =
            reviewAndBookInsuranceScannerShown &&
            currentCard?.insuranceCardImageUrls?.front;

        dispatch(
            updateBookingState({
                ...bookingState,
                [BookingStateProperties.insuranceCarrierId]: carrierId,
                [BookingStateProperties.insurancePlanId]: undefined,
                [BookingStateProperties.insuranceCardDataId]:
                    useExistingInsuranceCardDataId
                        ? currentCard.availableInsuranceCardDataId
                        : null,
                [BookingStateProperties.isInsuranceCardShared]: true,
                requirements: newRequirements,
            })
        );

        dispatch(
            setCard({
                cards: insuranceCards,
                card: currentCard,
                analytics: [
                    {
                        type: AnalyticsTypes.TrackEvent,
                        category: APPOINTMENT_INFO_GA_CATEGORY,
                        action: 'Insurance Carrier Selection',
                        eventDetails: {
                            InsuranceCarrierId: carrierId,
                            SelectionType: INSURANCE_ID_TYPE.CARRIER,
                        },
                    },
                ],
                isCarrierChange: true,
            })
        );
    };
}

export function setPlanState(planId = '-1') {
    return (dispatch, getState) => {
        const state = getState();
        const bookingState = bookingStateSelector(state);
        const { insuranceCards } = patientSelector(state);

        const currentCard = getCurrentInsuranceCard({
            state,
            insuranceCards,
            planId,
        });

        const requirements = get(bookingState, 'requirements', []);
        const newRequirements = requirements.filter(
            (req) => req.key !== INSURANCE_NOT_TAKEN_WARNING
        );

        dispatch(
            updateBookingState({
                ...bookingState,
                [BookingStateProperties.insurancePlanId]: planId,
                [BookingStateProperties.insuranceCardDataId]:
                    currentCard.availableInsuranceCardDataId,
                [BookingStateProperties.isInsuranceCardShared]: true,
                requirements: newRequirements,
            })
        );

        dispatch(
            setCard({
                cards: insuranceCards,
                card: currentCard,
                analytics: [
                    {
                        type: AnalyticsTypes.TrackEvent,
                        category: APPOINTMENT_INFO_GA_CATEGORY,
                        action: 'Insurance Plan Selection',
                        eventDetails: {
                            InsurancePlanId: planId,
                            SelectionType: INSURANCE_ID_TYPE.PLAN,
                        },
                    },
                ],
                isPlanChange: true,
            })
        );
    };
}

export function trackClickCarrierDropdown() {
    return {
        type: InsuranceCardActions.TRACK_CLICK_CARRIER_DROPDOWN,
        meta: {
            analytics: [
                {
                    type: AnalyticsTypes.TrackEvent,
                    category: APPOINTMENT_INFO_GA_CATEGORY,
                    action: GA_ACTIONS.ClickInsuranceCarrierDropdown,
                },
            ],
        },
    };
}

export function trackClickPlanDropdown() {
    return {
        type: InsuranceCardActions.TRACK_CLICK_PLAN_DROPDOWN,
        meta: {
            analytics: [
                {
                    type: AnalyticsTypes.TrackEvent,
                    category: APPOINTMENT_INFO_GA_CATEGORY,
                    action: GA_ACTIONS.ClickInsurancePlanDropdown,
                },
            ],
        },
    };
}

export function trackPreloadInsuranceCarrier(insuranceCarrierId) {
    return {
        type: InsuranceCardActions.TRACK_PRELOAD_CARRIER,
        meta: {
            analytics: [
                {
                    type: AnalyticsTypes.TrackEvent,
                    category: APPOINTMENT_INFO_GA_CATEGORY,
                    action: GA_ACTIONS.PreloadInsuranceCarrier,
                    eventDetails: {
                        InsuranceCarrierId: insuranceCarrierId,
                        SelectionType: INSURANCE_ID_TYPE.CARRIER,
                    },
                },
            ],
        },
    };
}

export function trackPreloadInsurancePlan(insurancePlanId) {
    return {
        type: InsuranceCardActions.TRACK_PRELOAD_PLAN,
        meta: {
            analytics: [
                {
                    type: AnalyticsTypes.TrackEvent,
                    category: APPOINTMENT_INFO_GA_CATEGORY,
                    action: GA_ACTIONS.PreloadInsurancePlan,
                    eventDetails: {
                        InsurancePlanId: insurancePlanId,
                        SelectionType: INSURANCE_ID_TYPE.PLAN,
                    },
                },
            ],
        },
    };
}

export function initializeCards() {
    return (dispatch, getState) => {
        const state = getState();
        const reviewAndBookInsuranceScannerShown =
            shouldShowReviewAndBookScanner(state);
        const bookingState = bookingStateSelector(state) || {};
        const stateCardId = (
            bookingState[BookingStateProperties.insuranceCardDataId] || -1
        ).toString();
        const isCardShared =
            bookingState[BookingStateProperties.isInsuranceCardShared] || false;
        const { insuranceCards } = patientSelector(state);
        const scanCardState = reviewAndBookScanCardStateSelector(state);

        const currentCard = getCurrentInsuranceCard({
            state,
            insuranceCards,
        });

        // If failed to fetch the scanned card in R&B
        if (
            reviewAndBookInsuranceScannerShown &&
            scanCardState.insuranceCardScanned &&
            currentCard.id === null
        ) {
            dispatch(handleFetchInsuranceCardRetry(currentCard));
        } else {
            const bookingStateIceRequestTokenUnusedState =
                bookingState[BookingStateProperties.iceRequestToken] &&
                bookingState[BookingStateProperties.iceRequestToken] !==
                    currentCard.iceRequestToken
                    ? { [BookingStateProperties.iceRequestToken]: '' }
                    : {};
            // If current card does not match card in state
            if (!currentCard.isDefault && currentCard.id !== stateCardId) {
                // If current card is from scanned card in R&B
                if (
                    reviewAndBookInsuranceScannerShown &&
                    scanCardState.insuranceCardScanned
                ) {
                    dispatch({
                        type: InsuranceCardActions.TRACK_SCANNED_CARD_RETRY_METRICS,
                        meta: {
                            analytics: {
                                type: AnalyticsTypes.TrackEvent,
                                category: APPOINTMENT_INFO_GA_CATEGORY,
                                action: 'Fetch Scanned Insurance Card for ICE Token Success',
                                eventDetails: {
                                    iceToken:
                                        scanCardState.scannedIceRequestToken,
                                    retryCount:
                                        scanCardState.scannedCardFetchRetryCount,
                                },
                            },
                        },
                    });

                    dispatch(setScannedCardFetchRetryCount(0));

                    dispatch(
                        updateBookingState({
                            ...bookingState,
                            [BookingStateProperties.insuranceCardDataId]:
                                currentCard.availableInsuranceCardDataId,
                            [BookingStateProperties.iceRequestToken]:
                                scanCardState.scannedIceRequestToken,
                            [BookingStateProperties.isInsuranceCardShared]: true,
                        })
                    );
                } else {
                    dispatch(
                        updateBookingState({
                            ...bookingState,
                            ...bookingStateIceRequestTokenUnusedState,
                            [BookingStateProperties.insuranceCardDataId]:
                                currentCard.availableInsuranceCardDataId,
                            [BookingStateProperties.isInsuranceCardShared]: true,
                        })
                    );
                }
            } else if (!currentCard.isDefault && !isCardShared) {
                dispatch(
                    updateBookingState({
                        ...bookingState,
                        ...bookingStateIceRequestTokenUnusedState,
                        [BookingStateProperties.isInsuranceCardShared]: true,
                    })
                );
            } else if (currentCard.isDefault) {
                dispatch(
                    updateBookingState({
                        ...bookingState,
                        ...bookingStateIceRequestTokenUnusedState,
                    })
                );
            }

            dispatch(
                setCard({
                    cards: insuranceCards,
                    card: currentCard,
                })
            );

            dispatch(setScannedCardLoadingState(false));
            dispatch(setScannedCardScannerOpenState(false));
        }
    };
}

const handleFetchInsuranceCardRetry = (currentCard) => {
    return (dispatch, getState) => {
        const state = getState();
        const { insuranceCards } = patientSelector(state);
        const scanCardState = reviewAndBookScanCardStateSelector(state);

        const pastRetryCount = scanCardState.scannedCardFetchRetryCount;
        if (pastRetryCount >= SCANNED_CARD_FETCH_MAX_RETRIES) {
            dispatch({
                type: InsuranceCardActions.TRACK_SCANNED_CARD_RETRY_METRICS,
                meta: {
                    analytics: {
                        type: AnalyticsTypes.TrackEvent,
                        category: APPOINTMENT_INFO_GA_CATEGORY,
                        action: 'Fetch Scanned Insurance Card for ICE Token Permanently Failed',
                        eventDetails: {
                            iceToken: scanCardState.scannedIceRequestToken,
                        },
                    },
                },
            });

            logToDataDog({
                name: `booking_web_app.review_and_book.fetch_scanned_insurance_card_permanently_failed`,
                type: 'increment',
            });
            // Display error message
            dispatch(setFetchScannedCardError(true));
            dispatch(
                setCard({
                    cards: insuranceCards,
                    card: currentCard,
                })
            );
            dispatch(setScannedCardLoadingState(false));
            dispatch(setScannedCardScannerOpenState(false));
        } else {
            dispatch(setScannedCardFetchRetryCount(pastRetryCount + 1));
            dispatch({
                type: InsuranceCardActions.TRACK_SCANNED_CARD_RETRY_METRICS,
                meta: {
                    analytics: {
                        type: AnalyticsTypes.TrackEvent,
                        category: APPOINTMENT_INFO_GA_CATEGORY,
                        action: 'Retry Fetch Scanned Insurance Card for ICE Token',
                        eventDetails: {
                            iceToken: scanCardState.scannedIceRequestToken,
                            retryCount: pastRetryCount + 1,
                        },
                    },
                },
            });
            setTimeout(
                () => dispatch(fetchAndSetInsuranceCards(true)),
                SCANNED_CARD_FETCH_RETRY_TIMING[pastRetryCount]
            );
        }
    };
};

export function fetchAndSetInsuranceCards(
    fromReviewAndBookCardScanner = false
) {
    return async (dispatch, getState) => {
        try {
            await dispatch(getInsuranceCards(fromReviewAndBookCardScanner));
            dispatch(initializeCards());
        } catch (exception) {
            captureException(exception);
        }
    };
}

export function handleShareInsuranceCheckboxChange(isInsuranceCardShared) {
    return (dispatch, getState) => {
        const state = getState();
        const bookingState = bookingStateSelector(state);
        const { selectedInsuranceCard } = patientSelector(state);
        const { memberIdFromCard } = selectedInsuranceCard;

        dispatch({
            type: InsuranceCardActions.SHARE_CLICKED,
            payload: {
                isInsuranceCardShared,
                memberIdFromCard,
            },
            meta: {
                analytics: {
                    type: AnalyticsTypes.TrackEvent,
                    category: APPOINTMENT_INFO_GA_CATEGORY,
                    action: 'Toggled Insurance Card Checkbox',
                },
            },
        });

        dispatch(
            updateBookingState({
                ...bookingState,
                [BookingStateProperties.isInsuranceCardShared]:
                    isInsuranceCardShared,
            })
        );
    };
}

export function handleClearInsuranceFromModal() {
    return (dispatch, getState) => {
        const state = getState();
        const bookingState = bookingStateSelector(state);
        const { insuranceCards } = patientSelector(state);

        const currentCard = getCurrentInsuranceCard({
            state,
            insuranceCards,
        });

        dispatch(
            updateBookingState({
                ...bookingState,
                [BookingStateProperties.insuranceCarrierId]: '-2',
                [BookingStateProperties.insurancePlanId]: undefined,
                [BookingStateProperties.insuranceCardDataId]: null,
                [BookingStateProperties.isInsuranceCardShared]: true,
            })
        );

        dispatch(
            setCard({
                cards: insuranceCards,
                card: currentCard,
                analytics: [
                    {
                        type: AnalyticsTypes.TrackEvent,
                        category: 'Booking - Modal',
                        action: 'Clicked Update Insurance After Out of Network Warning',
                    },
                ],
                isCarrierChange: true,
            })
        );
    };
}

export function handleScanInsuranceCardSuccess(correlationId) {
    return {
        type: InsuranceCardActions.SCANNED_CARD_SUCCESS,
        payload: {
            correlationId: correlationId,
        },
    };
}

//true if scanner should be open, false is it should be closed
export function setScannedCardScannerOpenState(isScannerOpen) {
    return {
        type: InsuranceCardActions.SET_SCANNED_CARD_SCANNER_OPEN_STATE,
        payload: {
            isScannerOpen: isScannerOpen,
        },
    };
}

//true if waiting for scanned card to be saved/fetched from monolith
export function setScannedCardLoadingState(isLoading) {
    return {
        type: InsuranceCardActions.SET_SCANNED_CARD_LOADING_STATE,
        payload: {
            isLoading: isLoading,
        },
    };
}

export function setScannedCardFetchRetryCount(retryCount) {
    return {
        type: InsuranceCardActions.SET_SCANNED_CARD_FETCH_RETRY_COUNT,
        payload: {
            retryCount: retryCount,
        },
    };
}

export function setFetchScannedCardError(hasFetchError) {
    return {
        type: InsuranceCardActions.SET_HAS_FETCH_SCANNED_CARD_ERROR,
        payload: {
            hasFetchError: hasFetchError,
        },
    };
}

export function setIsFetchingInsuranceCards(isFetching) {
    return {
        type: InsuranceCardActions.SET_IS_FETCHING_CARDS,
        payload: isFetching,
    };
}
